import { UnstyledButton } from '@mantine/core';
import { FC, Fragment, startTransition, useEffect, useMemo, useState } from 'react';

import Paragraph from '@/components/text/Paragraph.tsx';

import { AppointmentWithPosition, CalendarPosition, GroupRenderData } from '@/models/Calendar.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';

import { useCalendarStore } from '@/stores/calendarStore.ts';

import { calculateAppointmentTop, calculateGroupPositions } from '@/utils/calendarUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

import CircularLoader from '../CircularLoader.tsx';
import AppointmentCard from './AppointmentCard.tsx';

interface Props {
    groupedAppointments: Record<string, DailyAppointment[]>;
    timeslotHeight: number;
    containerHeight: number;
    appointmentColors: Record<string, string>;
    mutateAppointments: () => void;
    date?: Date;
}

const BATCH_SIZE = 300; // Number of appointments to render per batch
const BATCH_DELAY = 8; // Delay between batches in ms
const MAX_APPOINTMENTS_IN_COLLAPSED_GROUP = 3;
const EMPTY_POSITIONS_MAP = new Map<string, CalendarPosition>();

interface GroupPositionData {
    appointments: AppointmentWithPosition[];
    collapsedPositions: Map<string, CalendarPosition>;
    hasHidden: boolean;
}

const GroupedAppointmentsList: FC<Props> = ({
    groupedAppointments,
    timeslotHeight,
    containerHeight,
    appointmentColors,
    mutateAppointments,
    date,
}) => {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [renderedBatches, setRenderedBatches] = useState<Record<string, number>>({});
    const [isRendering, setIsRendering] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState<string | undefined>(undefined);

    const isWeekViewEnabled = useCalendarStore((state) => state.is_week_view_enabled);
    const timelineInterval = useCalendarStore((state) => state.timeline_interval);

    const toggleGroupExpansion = (group_id: string): void => {
        setIsLoading(group_id);
        setExpandedGroups((prev) => {
            const newGroups = new Set(prev);
            if (newGroups.has(group_id)) {
                newGroups.delete(group_id);
            } else {
                newGroups.add(group_id);
            }
            return newGroups;
        });
    };

    // Compute expanded + collapsed positions once per data change (not per expand/collapse toggle)
    const groupPositions = useMemo((): Record<string, GroupPositionData> => {
        const result: Record<string, GroupPositionData> = {};

        for (const [groupId, appointments] of Object.entries(groupedAppointments)) {
            const positions = calculateGroupPositions(appointments);
            const withPositions = appointments.map((appointment) => ({
                ...appointment,
                position: positions.get(appointment.id) ?? { left: 0, width: 100, column_position: 0 },
            }));

            const visibleWhenCollapsed = withPositions.filter(
                (app) => app.position.column_position < MAX_APPOINTMENTS_IN_COLLAPSED_GROUP
            );
            const hasHidden = visibleWhenCollapsed.length < withPositions.length;
            const collapsedPositions = hasHidden ? calculateGroupPositions(visibleWhenCollapsed) : EMPTY_POSITIONS_MAP;

            result[groupId] = {
                appointments: withPositions,
                collapsedPositions: collapsedPositions,
                hasHidden: hasHidden,
            };
        }

        return result;
    }, [groupedAppointments]);

    const renderData = useMemo((): Record<string, GroupRenderData> => {
        const groups: Record<string, GroupRenderData> = {};

        for (const [groupId, group] of Object.entries(groupPositions)) {
            const { appointments: appointmentsInGroup, collapsedPositions, hasHidden } = group;
            const isExpanded = expandedGroups.has(groupId);

            const visibleAptsWhenCollapsed = appointmentsInGroup.filter(
                (app) => app.position.column_position < MAX_APPOINTMENTS_IN_COLLAPSED_GROUP
            );

            const hasExpandButton = isWeekViewEnabled && hasHidden;

            const visibleAppointments =
                isWeekViewEnabled && !isExpanded ? visibleAptsWhenCollapsed : appointmentsInGroup;

            const groupStartTop =
                appointmentsInGroup.length > 0
                    ? Math.min(
                          ...appointmentsInGroup.map((apt) =>
                              calculateAppointmentTop(apt.start, containerHeight, timeslotHeight, timelineInterval)
                          )
                      )
                    : 0;

            groups[groupId] = {
                appointments_in_group: appointmentsInGroup,
                visible_when_collapsed: visibleAptsWhenCollapsed,
                final_appointments: visibleAppointments,
                collapsed_positions: collapsedPositions,
                has_hidden: hasExpandButton,
                hidden_count: appointmentsInGroup.length - visibleAptsWhenCollapsed.length,
                group_start_top: groupStartTop,
            };
        }

        return groups;
    }, [groupPositions, expandedGroups, isWeekViewEnabled, containerHeight, timeslotHeight, timelineInterval]);

    // Initialize batch rendering for each group
    useEffect(() => {
        const newRenderedBatches: Record<string, number> = {};
        const newIsRendering: Record<string, boolean> = {};

        for (const groupId of Object.keys(renderData)) {
            const group = renderData[groupId];
            const totalAppointments = group.final_appointments.length;

            if (totalAppointments > 0) {
                const isExpanded = expandedGroups.has(groupId);

                // Always batch load for collapsed groups
                // Never batch load for expanded groups
                if (isExpanded) {
                    // Render all appointments at once for expanded groups
                    newRenderedBatches[groupId] = Math.ceil(totalAppointments / BATCH_SIZE);
                    newIsRendering[groupId] = false;
                } else {
                    // Start with first batch for collapsed groups and show loading
                    newRenderedBatches[groupId] = 1;
                    newIsRendering[groupId] = Math.ceil(totalAppointments / BATCH_SIZE) > 1;
                }
            } else {
                newRenderedBatches[groupId] = 0;
                newIsRendering[groupId] = false;
            }
        }
        // eslint-disable-next-line react-hooks/set-state-in-effect,react-you-might-not-need-an-effect/no-adjust-state-on-prop-change,react-you-might-not-need-an-effect/no-chain-state-updates
        setIsLoading(undefined);

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change,react-you-might-not-need-an-effect/no-chain-state-updates
        setRenderedBatches(newRenderedBatches);
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change,react-you-might-not-need-an-effect/no-chain-state-updates
        setIsRendering(newIsRendering);
    }, [renderData, expandedGroups]);

    // Progressive rendering for each group
    useEffect(() => {
        // Always allow progressive rendering for collapsed groups
        const timeouts: NodeJS.Timeout[] = [];

        for (const [groupId, group] of Object.entries(renderData)) {
            const currentBatch = renderedBatches[groupId] || 0;
            const totalBatches = Math.ceil(group.final_appointments.length / BATCH_SIZE);
            const isExpanded = expandedGroups.has(groupId);

            // Skip batch loading for expanded groups
            if (isExpanded) {
                continue;
            }

            if (isRendering[groupId] && currentBatch < totalBatches) {
                const timeoutId = setTimeout(() => {
                    startTransition(() => {
                        setRenderedBatches((prev) => ({
                            ...prev,
                            [groupId]: prev[groupId] + 1,
                        }));

                        // Stop loading only when all batches are rendered
                        if (currentBatch + 1 >= totalBatches) {
                            setIsRendering((prev) => ({
                                ...prev,
                                [groupId]: false,
                            }));
                        }
                    });
                }, BATCH_DELAY * currentBatch);

                timeouts.push(timeoutId);
            }
        }

        return (): void => {
            for (const timeoutId of timeouts) {
                clearTimeout(timeoutId);
            }
        };
    }, [renderData, renderedBatches, isRendering, expandedGroups]);

    return (
        <>
            {Object.entries(renderData).map(([group_id, group]) => {
                const isExpanded = expandedGroups.has(group_id);

                // Create unique key using date (if available) and group_id
                const uniqueKey = date ? `${dayjs(date).format('YYYY-MM-DD')}_${group_id}` : group_id;
                const isGroupLoading = isLoading === group_id && group.final_appointments.length > 50;

                return (
                    <Fragment key={uniqueKey}>
                        {group.has_hidden && (
                            <UnstyledButton
                                onClick={() => toggleGroupExpansion(group_id)}
                                style={{ top: `${group.group_start_top}%` }}
                                className="absolute right-1.5 z-30 rounded border border-slate-300 bg-white px-1.5 py-0.5 shadow-md transition-all duration-150 hover:bg-slate-50 hover:shadow-xl"
                            >
                                {isGroupLoading ? (
                                    <CircularLoader />
                                ) : (
                                    <Paragraph className="text-xs font-medium text-slate-700">
                                        {isExpanded ? 'ausblenden' : `+${group.hidden_count}`}
                                    </Paragraph>
                                )}
                            </UnstyledButton>
                        )}
                        {!isGroupLoading &&
                            group.final_appointments
                                .slice(0, (renderedBatches[group_id] || 0) * BATCH_SIZE)
                                .map((appointment) => (
                                    <AppointmentCard
                                        key={appointment.id}
                                        appointment={appointment}
                                        timeslotHeight={timeslotHeight}
                                        containerHeight={containerHeight}
                                        color={appointmentColors[appointment.id]}
                                        preCalculatedPosition={
                                            isExpanded || !isWeekViewEnabled || !group.has_hidden
                                                ? appointment.position
                                                : (group.collapsed_positions.get(appointment.id) ??
                                                  appointment.position)
                                        }
                                        mutateAppointments={mutateAppointments}
                                    />
                                ))}
                    </Fragment>
                );
            })}
        </>
    );
};

export default GroupedAppointmentsList;
